"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Lightweight scroll-reveal. Fades + slides children up the first time they
 * enter the viewport. No dependency (IntersectionObserver). Respects
 * prefers-reduced-motion.
 */
export default function Reveal({ children, className = "", delay = 0, as: Tag = "div", dir = "up" }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setShown(true);
      return;
    }
    const revealIfVisible = () => {
      const rect = el.getBoundingClientRect();
      const isNearViewport =
        rect.top < window.innerHeight * 1.05 && rect.bottom > window.innerHeight * -0.05;
      if (!isNearViewport) return false;
      setShown(true);
      return true;
    };

    if (revealIfVisible()) {
      return;
    }

    const fallback = window.setTimeout(() => setShown(true), 900);
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          io.disconnect();
          window.clearTimeout(fallback);
          window.removeEventListener("scroll", revealIfVisible);
          window.removeEventListener("resize", revealIfVisible);
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);
    window.addEventListener("scroll", revealIfVisible, { passive: true });
    window.addEventListener("resize", revealIfVisible);
    return () => {
      io.disconnect();
      window.clearTimeout(fallback);
      window.removeEventListener("scroll", revealIfVisible);
      window.removeEventListener("resize", revealIfVisible);
    };
  }, []);

  const hiddenOffset =
    { up: "translate-y-8", left: "-translate-x-8", right: "translate-x-8" }[dir] ||
    "translate-y-8";

  return (
    <Tag
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-700 ease-out motion-reduce:transition-none ${
        shown ? "opacity-100 translate-x-0 translate-y-0" : `opacity-0 ${hiddenOffset}`
      } ${className}`}
    >
      {children}
    </Tag>
  );
}
